import React, { useState } from "react";
import api from "../../api";
import { ScanLine, Search, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import QRScanner from "../../components/QRScanner";

export default function RedeemTab() {
  const [code, setCode] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [scan, setScan] = useState(false);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingType, setPendingType] = useState(null);

  const lookup = async (c) => {
    const val = (c || code).trim().toUpperCase();
    if (!val) return;
    setCode(val); setLoading(true); setVoucher(null); setPendingType(null);
    try {
      const { data } = await api.get(`/voucher/${val}`);
      setVoucher(data); setAmount(String(data.balance));
    } catch (err) {
      toast({ title: "Not found", description: "No voucher with that code", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const redeem = async (type, honor_expired = false) => {
    try {
      const { data } = await api.post("/business/redeem", { code, amount: parseFloat(amount) || 0, type, honor_expired });
      toast({ title: "Redeemed", description: voucher?.kind === "experience" ? "Experience marked as used." : `£${data.redeemed.toFixed(2)} redeemed. New balance £${data.balance.toFixed(2)}.` });
      lookup(code);
    } catch (err) {
      if (err?.response?.status === 409) {
        // expired — ask to honour
        setPendingType(type);
        toast({ title: "Voucher expired", description: "Choose ‘Honour & redeem’ to accept it anyway.", variant: "destructive" });
      } else {
        toast({ title: "Error", description: err?.response?.data?.detail || "Redeem failed", variant: "destructive" });
      }
    }
  };

  return (
    <div className="max-w-[520px]">
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        <form onSubmit={(e) => { e.preventDefault(); lookup(); }} className="flex gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={10} placeholder="Enter voucher code"
            className="flex-1 font-mono tracking-wider rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-[#2ec9c9] uppercase" />
          <button className="gp-btn-teal rounded-xl px-4 font-medium flex items-center gap-2"><Search size={18} /></button>
          <button type="button" onClick={() => setScan(true)} className="gp-btn-outline rounded-xl px-4 font-medium flex items-center gap-2"><ScanLine size={18} /></button>
        </form>

        {loading && <Loader2 className="animate-spin text-[#2ec9c9] mt-6 mx-auto" size={28} />}

        {voucher && (
          <div className="mt-6 border-t border-gray-100 pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-[#8a97a8]">{voucher.business_name} — {voucher.product_title}</p>
                {voucher.kind === "experience" ? (
                  <p className="text-2xl font-bold text-[#16243b] mt-1" data-testid="redeem-experience-label">1 × experience</p>
                ) : (<>
                <p className="text-3xl font-bold text-[#16243b] mt-1">£{voucher.balance.toFixed(2)}</p>
                <p className="text-xs text-[#8a97a8]">of £{voucher.original_amount.toFixed(2)}</p>
                </>)}
              </div>
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${voucher.expired && voucher.status !== "used" ? "bg-red-100 text-red-600" : "bg-[#eefafa] text-[#1fb0b0]"}`}>
                {voucher.expired && voucher.status !== "used" ? "expired" : voucher.status.replace("_", " ")}
              </span>
            </div>

            {voucher.expires_at && (
              <p className="text-xs text-[#8a97a8] mt-2">{voucher.expired ? "Expired on" : "Valid until"} {voucher.expires_at.slice(0, 10)}</p>
            )}

            {voucher.status === "used" ? (
              <div className="mt-6 bg-red-50 text-red-600 rounded-xl p-4 flex items-center gap-2 text-sm"><CheckCircle2 size={18} /> This voucher is fully redeemed.</div>
            ) : (
              <div className="mt-6 space-y-3">
                {voucher.kind !== "experience" && <div>
                  <label className="block text-sm font-medium text-[#16243b] mb-1.5">Amount to redeem (£)</label>
                  <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="redeem-amount-input"
                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
                </div>}
                {voucher.expired && !voucher.honored ? (
                  <div className="space-y-3">
                    <div className="bg-amber-50 border border-amber-100 text-amber-700 rounded-xl p-3 text-sm flex items-start gap-2">
                      <AlertTriangle size={18} className="mt-0.5 shrink-0" /> This voucher has expired. You can still choose to honour it.
                    </div>
                    <div className="flex gap-3">
                      {voucher.kind !== "experience" && <button onClick={() => redeem("partial", true)} data-testid="redeem-honour-part-btn" className="flex-1 gp-btn-outline rounded-xl py-3 font-medium">Honour & redeem part</button>}
                      <button onClick={() => redeem("full", true)} data-testid="redeem-honour-full-btn" className="flex-1 gp-btn-teal rounded-xl py-3 font-medium">{voucher.kind === "experience" ? "Honour & mark used" : "Honour & redeem full"}</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    {voucher.kind !== "experience" && <button onClick={() => redeem("partial")} data-testid="redeem-part-btn" className="flex-1 gp-btn-outline rounded-xl py-3 font-medium">Redeem part</button>}
                    <button onClick={() => redeem("full")} data-testid="redeem-full-btn" className="flex-1 gp-btn-teal rounded-xl py-3 font-medium">{voucher.kind === "experience" ? "Mark experience as used" : "Redeem full balance"}</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {scan && <QRScanner onResult={(c) => { setScan(false); lookup(c); }} onClose={() => setScan(false)} />}
    </div>
  );
}
